import { sessionApiClient } from "@planningcenter/cc-api-client"
import {
  ConfirmationToken,
  Stripe,
  StripeElements,
  loadStripe,
} from "@stripe/stripe-js"
import {
  Config,
  CreateDonationPayload,
  DateStampString,
  CreateRecurringDonationPayload,
  StripeCustomerSessionClientSecret,
  StripePaymentMethodDataWithMetadata,
  FormSubmissionResult,
  DonationStatusResult,
  EditableFrequency,
} from "./types"
import { frequencyToSchedule } from "./utils/frequency"
import {
  DonationFormSubmissionError,
  RetryableDonationFormInvariantError,
} from "./errors"
import { APIError } from "source/shared/types"
import { buildSubdomain, buildUrl } from "@planningcenter/cc-url"

export async function fetchStartDates(
  frequency: EditableFrequency,
): Promise<DateStampString[]> {
  const schedule = frequencyToSchedule(frequency)
  const response = await sessionApiClient.post(
    "/giving/v2/recurring_donation_start_dates",
    { data: { attributes: { schedule } } },
  )
  return response.data.attributes.occurrences
}

// CSC: Customer Session Client
async function fetchStripeCSCSecret(): Promise<StripeCustomerSessionClientSecret> {
  // TODO: these are to verify that we are not refetching unnecessarily,
  // probably want to remove before launch?
  console.debug("Fetching stripe csc secret")
  const response = await sessionApiClient.post(
    "/giving/v2/me/stripe_customer_session",
  )
  return response.data.attributes.client_secret
}
export function fetchStripeCSCSecretQuery() {
  return {
    queryKey: ["DonationForm", "stripeCustomerSessionClientSecret"],
    queryFn: fetchStripeCSCSecret,
    staleTime: Infinity,
  }
}

async function fetchStripeObject(config: Config) {
  // TODO: these are to verify that we are not refetching unnecessarily,
  // probably want to remove before launch?
  console.debug("Fetching stripe object")
  return await loadStripe(config.stripe_publishable_key, {
    stripeAccount: config.stripe_account_identifier,
  })
}
export function fetchStripeObjectQuery(config: Config) {
  return {
    queryKey: [
      "DonationForm",
      "stripeObject",
      config.stripe_publishable_key,
      config.stripe_account_identifier,
    ],
    queryFn: () => fetchStripeObject(config),
    staleTime: Infinity,
  }
}

export async function createStripeConfirmationToken({
  stripe,
  stripeElements,
  billingEmail,
  organizationId,
}: {
  stripe: Stripe
  stripeElements: StripeElements
  billingEmail: string
  organizationId: number
}): Promise<ConfirmationToken | null> {
  // TODO: these are to verify that we are not refetching unnecessarily,
  // probably want to remove before launch?
  console.debug("Creating confirmation token")

  const { error: submitError } = await stripeElements.submit()
  if (submitError?.code === "incomplete") {
    return null
  }

  const confirmationTokenResult = await stripe.createConfirmationToken({
    elements: stripeElements,
    params: {
      payment_method_data: {
        billing_details: {
          email: billingEmail,
        },
        allow_redisplay: "always",
        metadata: {
          planning_center_source_app: "Giving",
          planning_center_organization_id: organizationId,
        },
      } as StripePaymentMethodDataWithMetadata,
    },
  })
  if (confirmationTokenResult.confirmationToken) {
    return confirmationTokenResult.confirmationToken
  } else {
    throw new RetryableDonationFormInvariantError()
  }
}

export async function createDonation({
  payload,
}: {
  payload: CreateDonationPayload
}): Promise<FormSubmissionResult> {
  // TODO: these are to verify that we are not refetching unnecessarily,
  // probably want to remove before launch?
  console.debug("Creating donation")
  const path = "/giving/v2/create_donation"
  try {
    const response = await sessionApiClient.post(path, payload)
    return {
      id: response.data.id,
      recurring: false,
      awaitingVerification: response.data.attributes.awaiting_verification,
      paymentStatus: response.data.attributes.payment_status,
      delayed: response.data.attributes.delayed,
    }
  } catch (error: APIError | unknown) {
    if ((error as APIError).errors) {
      throw DonationFormSubmissionError.fromApiError(error as APIError, {
        path,
      })
    } else {
      throw DonationFormSubmissionError.fromUnknownError(error, {
        path,
      })
    }
  }
}

export async function createRecurringDonation({
  payload,
}: {
  payload: CreateRecurringDonationPayload
}): Promise<FormSubmissionResult> {
  // TODO: these are to verify that we are not refetching unnecessarily,
  // probably want to remove before launch?
  console.debug("Creating recurring donation")
  const path = "/giving/v2/me/recurring_donations"
  try {
    const response = await sessionApiClient.post(path, payload)
    return {
      id: response.data.id,
      recurring: true,
      awaitingVerification: response.data.attributes.awaiting_verification,
    }
  } catch (error: APIError | unknown) {
    if ((error as APIError).errors) {
      throw DonationFormSubmissionError.fromApiError(error as APIError, {
        path,
      })
    } else {
      throw DonationFormSubmissionError.fromUnknownError(error, {
        path,
      })
    }
  }
}

export async function sendNondeterministicErrorNotifications({
  path,
  responseStatus,
}: {
  path: string
  responseStatus: string
}) {
  // TODO: Replace with better debugging mechanism
  console.debug("Sending nondeterministic error notifications")
  await sessionApiClient.post("/giving/v2/nondeterministic_errors", {
    data: {
      attributes: {
        href: buildUrl(path, buildSubdomain("api")),
        response_status: responseStatus,
      },
    },
  })
}

export async function getDonationStatus({
  id,
}: {
  id: string
}): Promise<DonationStatusResult> {
  // TODO: Replace with better debugging mechanism
  console.debug("Getting donation status")
  const response = await sessionApiClient.get(
    `/giving/v2/donation_statuses/${id}`,
  )
  return {
    paymentStatus: response.data.attributes.payment_status,
    message: response.data.attributes.message,
  }
}
